import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  Card, CardContent, FormControlLabel, Switch, Typography,
} from '@mui/material';
import { DynamicTable } from 'components';
import { axiosInstance } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';

const fmt = (n) => Number(n || 0).toLocaleString();

// Columns compared as numbers when sorting; everything else compares as text.
const NUMERIC_COLS = new Set(['logins', 'active_days', 'notes']);
const DEFAULT_QUERY = { limit: 25, offset: 0, order: [['logins', 'DESC']] };

// Local stand-in for the server-side filtering DynamicTable's column filters expect. The
// table writes typed text into query.where as { $like: '%text%' }, a quoted "text" as
// { $eq: text }, and a column with several filterFields under where.$or. Matching is
// case-insensitive, like MySQL's default collation.
const matchesCond = (value, cond) => {
  const v = String(value == null ? '' : value).toLowerCase();
  if (cond && typeof cond === 'object') {
    if (cond.$eq !== undefined) return v === String(cond.$eq).toLowerCase();
    if (cond.$like !== undefined) {
      return v.includes(String(cond.$like).replace(/^%|%$/g, '').toLowerCase());
    }
    return true; // an operator this card doesn't use; don't hide rows over it
  }
  return v.includes(String(cond).toLowerCase());
};
const applyWhere = (rows, where) => {
  if (!where) return rows;
  const { $or: anyOf, ...allOf } = where;
  return rows.filter((r) => Object.entries(allOf).every(([k, c]) => matchesCond(r[k], c))
    && (!anyOf || Object.entries(anyOf).some(([k, c]) => matchesCond(r[k], c))));
};

const compareBy = (key, dir) => (a, b) => {
  let va = a[key];
  let vb = b[key];
  let cmp;
  if (NUMERIC_COLS.has(key)) {
    cmp = (Number(va) || 0) - (Number(vb) || 0);
  } else {
    // Nulls (e.g. never logged in) always sink to the bottom, whichever direction.
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    va = String(va).toLowerCase();
    vb = String(vb).toLowerCase();
    cmp = va.localeCompare(vb);
  }
  return dir === 'asc' ? cmp : -cmp;
};

// "User Logins" card on Activity Notes > Reports: the selected client's own users (Doctivity
// staff excluded server-side) with last login, logins, active days and notes over the page's
// date range. A "Show internal (Doctivity) users" switch (any user) also lists Doctivity staff
// who are members of this client or who worked in it during the range. Follows the page's user and encounter type filters like the other cards; the
// encounter type only affects Notes.
//
// The server returns the client's whole roster in one response, so filtering, sorting and
// paging are all local. The User and Email column filters are applied by applyWhere above;
// the date and count columns have no filter box. Logins are per user (sign-in carries no client); Active Days and Notes are
// specific to this client — see getUserLoginMetrics in doctivity-rest analyticsService.
export function UserLoginMetricsCard({ dateRange, encounterType, filterUser }) {
  const clientId = useSelector(({ app }) => app.selectedClient);
  const [showInternal, setShowInternal] = useState(false);
  const [users, setUsers] = useState(null);
  const [query, setQuery] = useState(DEFAULT_QUERY);

  const startISO = dateRange.startDate.toISOString();
  const endISO = dateRange.endDate.toISOString();
  const filterUserId = filterUser && filterUser !== 'All Users' ? filterUser.id : undefined;

  useEffect(() => {
    if (!clientId) return undefined;
    let alive = true;
    // Same filter shape the other Activity Notes report cards send.
    const filter = {
      client_id: clientId,
      user_id: filterUserId,
      comment_date: { $gte: startISO, $lte: endISO },
    };
    if (encounterType !== 'All') {
      filter.encounter_type_id = encounterType === 0 ? null : encounterType;
    }
    if (showInternal) {
      filter.show_internal = true;
    }
    setUsers(null);
    axiosInstance
      .post('/Analytics', { type: 'USER_LOGIN_METRICS', filter }, { params: { selected_client_id: clientId } })
      .then((r) => { if (alive) setUsers(r.data); })
      .catch(() => { if (alive) setUsers({ rows: [] }); });
    // A new result set is a new list — back to page 1 rather than a page that may be gone.
    setQuery((prev) => ({ ...prev, offset: 0 }));
    return () => { alive = false; };
  }, [clientId, startISO, endISO, encounterType, filterUserId, showInternal]);

  const rows = (users && users.rows) || [];
  const filtered = applyWhere(rows, query.where);

  const [sortKey, sortDir] = (query.order && query.order[0]) || DEFAULT_QUERY.order[0];
  const sorted = filtered.slice().sort(compareBy(sortKey, String(sortDir).toLowerCase()));
  const pageRows = sorted.slice(query.offset, query.offset + query.limit);

  // Totals over the filtered set, so the headline follows the column filters.
  const sum = (k) => filtered.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const rangeLabel = `${format(dateRange.startDate, 'M/d/yy')} to ${format(dateRange.endDate, 'M/d/yy')}`;
  const totals = users
    ? `${fmt(filtered.length)} users · ${fmt(sum('logins'))} logins · ${fmt(sum('notes'))} notes`
    : 'Loading…';

  const columns = [
    {
      label: 'User',
      key: 'last_name',
      // Typing in the User filter matches either name.
      filterFields: ['last_name', 'first_name'],
      // Staff rows only appear while "Show internal" is on, and are labelled so
      // they are never mistaken for the client's own users.
      format: (v, row) => {
        const name = `${row.last_name || ''}, ${row.first_name || ''}`;
        return Number(row.is_internal) ? `${name} (Doctivity)` : name;
      },
    },
    { label: 'Email', key: 'email' },
    {
      label: 'Last Login',
      key: 'last_login',
      showFilter: false,
      format: (v) => (v ? DateUtil.formatDateTimeFromDB(v) : '—'),
    },
    { label: 'Logins', key: 'logins', showFilter: false },
    { label: 'Active Days', key: 'active_days', showFilter: false },
    { label: 'Notes', key: 'notes', showFilter: false },
  ];

  return (
    <Card>
      <CardContent>
        <Typography component='h2' variant='h6' color='primary'>
          User Logins
        </Typography>
        <Typography fontWeight='300' color='black' style={{ marginBottom: 8 }}>
          {rangeLabel} | {totals}
        </Typography>
        <Typography variant='caption' color='textSecondary' component='p' style={{ marginBottom: 12 }}>
          Logins are counted per user across all of their clients. Active Days counts days with
          activity in this client. Notes follow the
          encounter type filter.
        </Typography>

        <div style={{ marginBottom: 8 }}>
          <FormControlLabel
            control={(
              <Switch
                size='small'
                checked={showInternal}
                onChange={(e) => setShowInternal(e.target.checked)}
              />
            )}
            label='Show internal (Doctivity) users'
            title='Client numbers exclude Doctivity staff by default. Turn on to also list staff who are members of this client or who worked in it during the range; they are labelled (Doctivity).'
            style={{ marginLeft: 0 }}
          />
        </div>

        <DynamicTable
          columns={columns}
          data={users ? { count: filtered.length, rows: pageRows } : { loading: true }}
          query={query}
          // DynamicTable mutates the query object it was given, so spread into a fresh
          // object or React will not see the change.
          onQueryChange={(q) => setQuery({ ...q })}
          idKey='user_id'
          noDataMessage={filtered.length < rows.length ? 'No users match those filters' : 'No users in this range'}
        />
      </CardContent>
    </Card>
  );
}

UserLoginMetricsCard.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
  }).isRequired,
  encounterType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  filterUser: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};
